import { JobDocumentTypeEnum } from "../../job-page/job-document/job-document.types";
import { FieldType } from "./formTypes";

export type RuleOperator =
	| "equals"
	| "not_equals"
	| "includes"
	| "not_includes"
	| "is_empty"
	| "is_not_empty"
	| "greater_than"
	| "less_than";

export const FIELD_TYPE_OPERATORS: Record<
	Exclude<FieldType, "description">,
	RuleOperator[]
> = {
	text: [
		"equals",
		"not_equals",
		"includes",
		"not_includes",
		"is_empty",
		"is_not_empty",
	],
	textarea: [
		"equals",
		"not_equals",
		"includes",
		"not_includes",
		"is_empty",
		"is_not_empty",
	],
	radio: ["equals", "not_equals", "is_empty", "is_not_empty"],
	chips: ["equals", "not_equals", "is_empty", "is_not_empty"],
	checkbox: ["includes", "not_includes", "is_empty", "is_not_empty"],
	select: ["equals", "not_equals", "is_empty", "is_not_empty"],
	file: ["is_empty", "is_not_empty"],
	odontogram: ["is_empty", "is_not_empty"],
	appointment: ["is_empty", "is_not_empty"],
	date: [
		"equals",
		"not_equals",
		"greater_than",
		"less_than",
		"is_empty",
		"is_not_empty",
	],
	time: [
		"equals",
		"not_equals",
		"greater_than",
		"less_than",
		"is_empty",
		"is_not_empty",
	],
	title: [],
};

// German labels for operators
export const OPERATOR_LABELS: Record<RuleOperator, string> = {
	equals: "ist gleich",
	not_equals: "ist nicht gleich",
	includes: "enthält",
	not_includes: "enthält nicht",
	is_empty: "ist leer",
	is_not_empty: "ist nicht leer",
	greater_than: "ist größer als",
	less_than: "ist kleiner als",
};

export interface RuleCondition {
	fieldId: string;
	operator: RuleOperator;
	value?: string | string[] | boolean | number;
}

// AND / OR connections of conditions
export interface RuleConditionGroup {
	type: "and" | "or";

	// For now, we only support flat rules
	// in the future, this could be nested: (RuleCondition | RuleConditionGroup)[];
	conditions: RuleCondition[];
}

// Prefillable job fields
export const PREFILLABLE_JOB_FIELDS = [
	"jobs.title",
	"jobs.incoming_order_date",
	"jobs.incoming_order_time",
	"jobs.outgoing_order_date",
	"jobs.outgoing_order_time",
	"jobs.description",
	"jobs.tooth_color",
] as const;

// Patient fields that can be used for matching an existing patient
export const PATIENT_MATCH_FIELDS = [
	"patients.first_name",
	"patients.last_name",
	"patients.birth_date",
	"patients.code",
] as const;

// Patient fields that can be pre-filled when creating a new patient
export const PATIENT_PREFILL_FIELDS = [
	"patients.title",
	"patients.first_name",
	"patients.last_name",
	"patients.code",
	"patients.birth_date",
	"patients.gender",
	"patients.street",
	"patients.additional_information",
	"patients.ahv_number",
] as const;

export type RuleActionType =
	| "create_job_document"
	| "prefill_job_fields"
	| "create_patient";

/**
 * Action type:
 * create_job_document
 *
 * Configuration:
 * documentType: user selects what type of document should be created
 * templateId: user selects the template (Vorlage/Jumbo) to create the document from
 */
export interface CreateJobDocumentAction {
	type: "create_job_document";
	documentType:
		| JobDocumentTypeEnum.Quotation
		| JobDocumentTypeEnum.DeliveryNote;
	templateId: number | null;
}

/**
 * Action type:
 * prefill_job_fields
 *
 * Configuration:
 * fieldMappings: user selects sets data for filling in the job fields
 */
export interface PrefillJobFieldsAction {
	type: "prefill_job_fields";
	fieldMappings: JobFieldMapping[];
}

/**
 * fieldMapping configuration for the prefill_job_fields action
 *
 * Configuration:
 * jobFieldKey: identifier of what field of the job should be pre-filled when creating the job
 * valueSource: describes the way of pre-filling the job field; either directly from a form field or as a custom value by the lab
 * formFieldId: if the job field is pre-filled from a form_field source, this is the id of that form field
 * customValue: if the job field is pre-filled from a custom value source, this is the value that will be used
 */
export interface JobFieldMapping {
	jobFieldKey: (typeof PREFILLABLE_JOB_FIELDS)[number];
	valueSource: "form_field" | "custom";
	formFieldId?: string; // If valueSource is "form_field"
	customValue?: string | number; // If valueSource is "custom"
}

/**
 * Action type:
 * create_patient
 *
 * Configuration:
 * matchFields: fields that need to match so that a patient is selected instead of created
 * fieldMappings: fields that will be pre-filled when creating a new patient (if not patient is found)
 */
export interface CreatePatientAction {
	type: "create_patient";
	matchFields: Array<(typeof PATIENT_MATCH_FIELDS)[number]>;
	fieldMappings: PatientFieldMapping[];
}

/**
 * fieldMapping configuration for the create_patient action
 *
 * Configuration:
 * patientFieldKey: identifier of what field of the patient should be pre-filled when creating the patient
 * valueSource: describes the way of pre-filling the patient field; either directly from a form field or as a custom value by the lab
 * formFieldId: if the patient field is pre-filled from a form_field source, this is the id of that form field
 * customValue: if the patient field is pre-filled from a custom value source, this is the value that will be used
 */
export interface PatientFieldMapping {
	patientFieldKey: (typeof PATIENT_PREFILL_FIELDS)[number];
	valueSource: "form_field" | "custom";
	formFieldId?: string;
	customValue?: string | number;
}

export type RuleAction =
	| CreateJobDocumentAction
	| PrefillJobFieldsAction
	| CreatePatientAction;

export interface FormRule {
	id: string;
	name: string;
	enabled: boolean;
	conditionGroup: RuleConditionGroup;
	action: RuleAction;
}
